import { useState } from "react";
import { toast } from "sonner";
import { CalendarBlank, ChatCircle, CheckCircle, PencilSimple, Trash, User } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { crewLabel } from "@/lib/permissions";
import { fmtTime12 } from "@/lib/scheduleWindows";

// One additional Template / Fabrication / Install stage on a job — full parity with
// the fixed stages: date, window, time, crew, areas, mark complete, stamp, text appt.
export function ExtraStopCard({ jobId, stop, onChanged, onEdit, onDelete, onTextAppt, apptBusy }) {
  const [marking, setMarking] = useState(false);
  const done = stop.stop_status?.status === "Done";

  const mark = async () => {
    setMarking(true);
    try {
      const { data } = await api.patch(
        `/jobs/${jobId}/stops/${encodeURIComponent(stop.kind || "Install")}`,
        { status: done ? "Pending" : "Done" },
        { params: { stop_id: stop.id } },
      );
      onChanged?.(data);
      toast.success(done ? `${stop.kind} reopened` : `${stop.kind} marked complete`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the stage");
    } finally {
      setMarking(false);
    }
  };

  return (
    <div data-testid={`extra-task-${stop.id}`} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-200">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: stop.color || "#71717a" }} />
          {stop.kind}
          {done && (
            <span data-testid={`extra-task-done-badge-${stop.id}`} className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-300 ring-1 ring-emerald-400/40">
              Complete
            </span>
          )}
        </span>
        <span className="font-mono-data inline-flex items-center gap-1 text-sm">
          <CalendarBlank size={14} /> {stop.date || "Not scheduled"}
        </span>
        {stop.date && (
          <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-300">
            {stop.time ? `${fmtTime12(stop.time)} · ${stop.window || "All day"}` : stop.window || "All day"}
          </span>
        )}
        {stop.label && (
          <span className="rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] text-indigo-200">{stop.label}</span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-400">
          <User size={13} /> {crewLabel(stop.crew)}
        </span>
        {stop.date && onTextAppt && (
          <button
            type="button"
            data-testid={`extra-task-appt-text-${stop.id}`}
            onClick={() => onTextAppt(stop)}
            disabled={apptBusy}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-indigo-500/50 bg-indigo-500/10 px-2.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20 disabled:opacity-50"
          >
            <ChatCircle size={12} weight="bold" /> {apptBusy ? "Sending…" : "Text appt"}
          </button>
        )}
        <button
          type="button"
          data-testid={`extra-task-mark-complete-${stop.id}`}
          disabled={marking}
          onClick={mark}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 disabled:opacity-50 ${
            done ? "border-zinc-700 text-zinc-400 hover:bg-zinc-800" : "border-emerald-400/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
          }`}
        >
          <CheckCircle size={12} weight="bold" /> {done ? "Reopen" : "Mark complete"}
        </button>
        {onEdit && (
          <button type="button" data-testid={`extra-task-edit-${stop.id}`} onClick={() => onEdit(stop)} title="Edit stage" className="text-zinc-500 transition-colors duration-150 hover:text-indigo-300">
            <PencilSimple size={14} weight="bold" />
          </button>
        )}
        {onDelete && (
          <button type="button" data-testid={`extra-task-delete-${stop.id}`} onClick={() => onDelete(stop.id)} title="Remove stage" className="text-zinc-500 transition-colors duration-150 hover:text-red-400">
            <Trash size={14} weight="bold" />
          </button>
        )}
      </div>
      {done && (
        <div data-testid={`extra-task-done-stamp-${stop.id}`} className="mt-1 text-[11px] text-emerald-300/80">
          Completed{" "}
          {stop.stop_status.done_at ? new Date(stop.stop_status.done_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}
          {stop.stop_status.done_by ? ` by ${stop.stop_status.done_by}` : ""}
        </div>
      )}
      {(stop.areas?.length > 0 || (stop.custom_fields || []).some((c) => c.label || c.value) || stop.notes?.length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
          {stop.areas?.length > 0 && <span data-testid={`extra-task-areas-${stop.id}`}>Areas: {stop.areas.join(", ")}</span>}
          {(stop.custom_fields || []).filter((c) => c.label || c.value).map((c, i) => (
            <span key={i}>{c.label}: <span className="text-zinc-300">{c.value}</span></span>
          ))}
          {stop.notes?.length > 0 && <span className="text-zinc-500">{stop.notes.length} note{stop.notes.length === 1 ? "" : "s"}</span>}
        </div>
      )}
    </div>
  );
}
